import React, { useEffect, useRef, useState } from 'react';
import './HyperstitionYoutubePlayer.css';

/**
 * HyperstitionYoutubePlayer
 * Optimized YouTube player for the Hyperstition module.
 * Minimizes YouTube UI, enforces muting, and uses a custom play button.
 */

let apiLoadingPromise = null;

const loadYoutubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiLoadingPromise) return apiLoadingPromise;

  apiLoadingPromise = new Promise((resolve) => {
    const existingOnReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (existingOnReady) existingOnReady();
      resolve();
    };

    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  });

  return apiLoadingPromise;
};

const HyperstitionYoutubePlayer = ({ videoId, title, filterCss, muted = true, controls = false }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);

  useEffect(() => {
    let player = null;
    let mounted = true;

    loadYoutubeApi().then(() => {
      if (!mounted || !containerRef.current) return;

      player = new window.YT.Player(containerRef.current, {
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          mute: muted ? 1 : 0,
          playsinline: 1,
          controls: controls ? 1 : 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          widget_referrer: window.location.href
        },
        events: {
          onReady: (event) => {
            if (!mounted) return;
            if (muted) {
                event.target.mute();
                event.target.setVolume(0);
            }
            setIsReady(true);
            // If autoplay was successful, hide the play button
            if (event.target.getPlayerState() === 1) {
              setShowPlayButton(false);
            }
          },
          onStateChange: (event) => {
            if (!mounted) return;
            // YT.PlayerState: 1 = PLAYING, 2 = PAUSED, 0 = ENDED, 3 = BUFFERING, -1 = UNSTARTED
            if (event.data === 1) {
              setShowPlayButton(false);
            } else if (event.data === 2 || event.data === 0 || event.data === -1) {
              setShowPlayButton(true);
            }
          }
        }
      });
      playerRef.current = player;
    });

    return () => {
      mounted = false;
      if (player && typeof player.destroy === 'function') {
        player.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, controls, muted]);

  useEffect(() => {
    if (!isReady || !playerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          try {
            if (muted) {
                playerRef.current.mute();
                playerRef.current.setVolume(0);
            }
            playerRef.current.playVideo();
          } catch (e) {
            console.warn("Intersection play failed", e);
          }
        } else {
          try {
            playerRef.current.pauseVideo();
          } catch (e) {}
        }
      },
      { threshold: 0.1 }
    );

    try {
      const iframe = playerRef.current.getIframe();
      if (iframe) {
        observer.observe(iframe);
      }
    } catch (e) {}

    return () => {
      try {
        const iframe = playerRef.current?.getIframe();
        if (iframe) {
          observer.unobserve(iframe);
        }
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, muted]);

  const handlePlayClick = (e) => {
    e.stopPropagation();
    if (playerRef.current && isReady) {
      try {
        if (muted) {
            playerRef.current.mute();
            playerRef.current.setVolume(0);
        }
        playerRef.current.playVideo();
        setShowPlayButton(false);
      } catch (e) {
        console.error("Manual play failed", e);
      }
    }
  };

  return (
    <div className="hyperstition-player-wrapper">
      <div ref={containerRef} className="hyperstition-player-container" style={{ filter: filterCss, pointerEvents: controls ? 'auto' : 'none' }}></div>
      {showPlayButton && !controls && (
        <div className="hyperstition-custom-play-overlay" onClick={handlePlayClick}>
           <button className="hyperstition-custom-play-btn" aria-label={title || 'Play video'}>
             <span className="play-icon">▶</span>
           </button>
        </div>
      )}
    </div>
  );
};

export default HyperstitionYoutubePlayer;
